
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useCurrentPlayer } from '@/hooks/useCurrentPlayer';
import { loadTimePeriods } from '@/stores/freeworldBootstrapStore';

interface TimePeriod {
  id: string;
  period_order: number;
  jump_duration_value: number | null;
}

export function useTimePeriodRestart() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { currentPlayer } = useCurrentPlayer();
  const [isRestarting, setIsRestarting] = useState(false);

  const getRestartCount = async (timePeriodId: string): Promise<number> => {
    if (!user?.id) return 0;
    if (!currentPlayer?.id) return 0;

    try {
      const { data, error } = await supabase
        .from('user_time_period_restarts')
        .select('id')
        .eq('player_id', currentPlayer.id)
        .eq('time_period_id', timePeriodId);

      if (error) {
        console.error('Error fetching restart count:', error);
        return 0;
      }

      return data?.length || 0;
    } catch (error) {
      console.error('Error fetching restart count:', error);
      return 0;
    }
  };

  const restartTimePeriod = async (timePeriodId: string, reason?: string): Promise<boolean> => {
    if (!user?.id) {
      toast({
        variant: "destructive",
        title: "Virhe",
        description: "Kirjaudu sisään aloittaaksesi kauden uudestaan."
      });
      return false;
    }
    if (!currentPlayer?.id) {
      toast({
        variant: "destructive",
        title: "Virhe",
        description: "Pelaajaa ei löydy. Lataa sivu ja yritä uudelleen."
      });
      return false;
    }

    setIsRestarting(true);

    try {
      // Log the restart (include both user_id and player_id)
      const { error: logError } = await supabase
        .from('user_time_period_restarts')
        .insert({
          user_id: user.id,
          player_id: currentPlayer.id,
          time_period_id: timePeriodId,
          restart_reason: reason
        });

      if (logError) {
        console.error('Error logging restart:', logError);
        throw logError;
      }

      // Reset user progress for this time period (by player)
      // Delete time task completions for this period
      const { error: tasksError } = await (supabase as any)
        .from('player_time_task_completions')
        .delete()
        .eq('player_id', currentPlayer.id)
        .eq('time_period_id', timePeriodId);

      if (tasksError) {
        console.error('Error deleting task completions:', tasksError);
        throw tasksError;
      }

      // Delete scenario sessions for this player that started during this period window
      const { error: sessionsError } = await supabase
        .from('user_scenario_sessions')
        .delete()
        .eq('player_id', currentPlayer.id)
        .gte('started_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()); // Last 30 days as approximation

      if (sessionsError) {
        console.error('Error deleting recent sessions:', sessionsError);
        // Don't throw here as this is less critical
      }

      // Reset character age to period start if needed
      try {
        // Get time periods data from centralized cache
        const timePeriods = await loadTimePeriods({ reason: 'useTimePeriodRestart.resetAge' });

        const currentPeriod = timePeriods.find(p => p.id === timePeriodId);
        if (currentPeriod && currentPeriod.period_order > 1) {
          const previousPeriod = timePeriods.find(p => p.period_order === currentPeriod.period_order - 1);
          if (previousPeriod) {
            // Calculate age based on previous period
            const baseAge = 15; // Starting age
            const ageIncrease = (currentPeriod.period_order - 1) * (previousPeriod.jump_duration_value || 1);
            
            const { error: characterError } = await supabase
              .from('character_profiles')
              .update({
                age: baseAge + ageIncrease,
                birth_year: new Date().getFullYear() - (baseAge + ageIncrease)
              })
              .eq('user_id', user.id);

            if (characterError) {
              console.error('Error resetting character age:', characterError);
            }
          }
        }
      } catch (periodsError) {
        console.error('Error handling time periods for character age reset:', periodsError);
        // Continue without failing the whole operation
      }

      // Reset financial status to beginning of this period
      // This is more complex and might need period-specific starting values
      const { error: financialError } = await supabase
        .from('player_financial_status')
        .update({
          current_time_period_id: timePeriodId,
          // Reset to some reasonable starting values for the period
          // In a real implementation, you'd want to store period starting values
        })
        .eq('player_id', currentPlayer.id);

      if (financialError) {
        console.error('Error resetting financial status:', financialError);
        // Don't throw as this can be handled separately
      }

      // Clear any incomplete progress state - by player
      const { error: progressError } = await supabase
        .from('user_progress_state')
        .delete()
        .eq('player_id', currentPlayer.id);

      if (progressError) {
        console.error('Error clearing progress state:', progressError);
      }

      toast({
        title: "Kausi aloitettu uudestaan",
        description: "Voit nyt tehdä uusia valintoja tässä kaudessa.",
        variant: "default"
      });

      // Trigger a page reload to reset the application state
      setTimeout(() => {
        window.location.reload();
      }, 1000);

      return true;

    } catch (error) {
      console.error('Error restarting time period:', error);
      toast({
        variant: "destructive",
        title: "Virhe kauden uudelleenaloituksessa",
        description: "Yritä uudelleen hetken päästä."
      });
      return false;
    } finally {
      setIsRestarting(false);
    }
  };

  return {
    restartTimePeriod,
    getRestartCount,
    isRestarting
  };
}
